// Pure logic behind the "Change Project, Protocol & Bus Type" view of the Edit
// Vehicle Details dialog (ChangeVehicleConfigView.jsx).
//
// getStringStructure mirrors the backend's reading of a battery config, so the
// "Strings 2 -> 3" preview shown before the change agrees with what the backend
// will store. Keep the two in step: if one side changes the rule, the preview
// lies about the vehicle's new structure.

import { translateErrorMessage } from "../Common/FieldTranslator";

export const MAX_BUS_TYPE_LENGTH = 200;

export const CRITICAL_UPDATE_FORBIDDEN_MESSAGE =
  "You do not have permission to change the vehicle configuration";

const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again.";

// The three request fields this view may send. The backend rejects a request
// that mixes them with any other update-details field.
export const CRITICAL_FIELDS = ["configID", "protocolID", "vehicleSubType"];

const isSpmc = (buildingBlockType) =>
  String(buildingBlockType ?? "").toUpperCase() === "SPMC";

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * String structure of a battery config option (a row of options.projects).
 * Only SPMC configs have strings; everything else is pack-level.
 *
 * @param {Object} config - { buildingBlockType, parallelPacks, seriesPacks, packCount }
 * @returns {{hasStrings: boolean, stringCount: number, packsPerString: number}}
 */
export const getStringStructure = (config) => {
  const hasStrings = isSpmc(config?.buildingBlockType);
  return {
    hasStrings,
    stringCount: hasStrings ? toCount(config?.parallelPacks) : 0,
    packsPerString: toCount(config?.seriesPacks) || toCount(config?.packCount) || 0,
  };
};

/**
 * String structure of the vehicle as it is now. buildVehicleConfigData
 * already resolves it (stringCount, packsPerString), so this only normalises
 * that shape to the one getStringStructure returns.
 */
export const getCurrentStringStructure = (current) => {
  const hasStrings = isSpmc(current?.buildingBlockType);
  return {
    hasStrings,
    stringCount: hasStrings ? toCount(current?.stringCount) : 0,
    packsPerString: toCount(current?.packsPerString),
  };
};

// Packs per string only means something when a side has strings; between two
// pack-level configs it would add a "None -> None" row to the review table.
export const isStringStructureChanged = (from, to) =>
  !!from?.hasStrings !== !!to?.hasStrings ||
  (from?.stringCount ?? 0) !== (to?.stringCount ?? 0) ||
  ((!!from?.hasStrings || !!to?.hasStrings) &&
    (from?.packsPerString ?? 0) !== (to?.packsPerString ?? 0));

// 280 -> "280 Ah", 280.5 -> "280.5 Ah". null when there is no usable number, so
// the caller decides how "missing" reads (CurrentValuesBox shows "N/A").
export const formatCapacity = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = parseFloat(value);
  if (isNaN(n)) return null;
  return `${parseFloat(n.toFixed(2))} Ah`;
};

// "2 × 4 packs", or "None (pack-level)" for a config without strings.
export const formatStrings = (structure) => {
  if (!structure?.hasStrings) return "None (pack-level)";
  return `${structure.stringCount} × ${structure.packsPerString} packs`;
};

// Secondary text beside a project in the dropdown: "350 Ah · 3 strings".
export const formatProjectSecondary = (project) => {
  const parts = [];
  const capacity = formatCapacity(project?.ratedCapacitySystem);
  if (capacity) parts.push(capacity);
  const structure = getStringStructure(project);
  if (structure.hasStrings) {
    parts.push(`${structure.stringCount} ${structure.stringCount === 1 ? "string" : "strings"}`);
  } else {
    parts.push("pack-level");
  }
  return parts.join(" · ");
};

// "Strings 2 → 3 · packs per string 4 → 4", or "Strings 2 → none (pack-level
// configuration)" when the target config has no strings.
export const formatStringTransition = (from, to) => {
  const fromCount = from?.hasStrings ? from.stringCount : "none";
  if (!to?.hasStrings) {
    return from?.hasStrings
      ? `Strings ${fromCount} → none (pack-level configuration)`
      : "Strings none (pack-level configuration)";
  }
  return (
    `Strings ${fromCount} → ${to.stringCount}` +
    ` · packs per string ${from?.packsPerString ?? 0} → ${to.packsPerString}`
  );
};

/**
 * When the new config has fewer strings than the vehicle does now, packs
 * recorded on the dropped strings stop showing in Components. Returns the
 * first string number that disappears, or null when nothing is dropped.
 * Going pack-level drops every string, so that returns 1.
 */
export const getFirstDroppedString = (from, to) => {
  if (!from?.hasStrings || !(from.stringCount > 0)) return null;
  if (!to?.hasStrings) return 1;
  return to.stringCount < from.stringCount ? to.stringCount + 1 : null;
};

const normaliseBusType = (value) => String(value ?? "").trim();

/**
 * Resolves typed bus type text against the known bus types. A case-insensitive
 * match takes the existing spelling (the backend does the same), so "12m ac"
 * becomes "12M AC" rather than a new group in every filter and chart.
 *
 * @returns {{value: string, isNew: boolean}} isNew is false for blank input.
 */
export const resolveBusType = (value, busTypes) => {
  const trimmed = normaliseBusType(value);
  if (!trimmed) return { value: "", isNew: false };
  const known = (busTypes || []).map(normaliseBusType);
  // An exact match wins, as on the backend: when the org already holds two
  // spellings that differ only in case, the one the user picked is kept.
  if (known.includes(trimmed)) return { value: trimmed, isNew: false };
  const needle = trimmed.toLowerCase();
  const match = known.find((b) => b.toLowerCase() === needle);
  return match !== undefined
    ? { value: match, isNew: false }
    : { value: trimmed, isNew: true };
};

export const isBusTypeChanged = (value, currentValue) => {
  const next = normaliseBusType(value);
  return !!next && next.toLowerCase() !== normaliseBusType(currentValue).toLowerCase();
};

/**
 * Bus type validation message, or null when valid. A vehicle that has a bus
 * type today cannot be cleared to blank from here; one that never had one may
 * stay blank while another field changes.
 */
export const getBusTypeError = (value, currentValue) => {
  const trimmed = normaliseBusType(value);
  if (trimmed.length > MAX_BUS_TYPE_LENGTH) {
    return `Bus type can be at most ${MAX_BUS_TYPE_LENGTH} characters`;
  }
  if (!trimmed && normaliseBusType(currentValue)) return "Please enter a bus type";
  return null;
};

const sameId = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && Number(a) === Number(b);

/**
 * The critical fields the draft changes, in CRITICAL_FIELDS order.
 *
 * @param {Object} current - buildVehicleConfigData().current
 * @param {Object} draft   - { configID, protocolID, busType } (busType: resolved text)
 * @returns {string[]} subset of CRITICAL_FIELDS
 */
export const diffConfig = (current, draft) => {
  const changed = [];
  if (draft?.configID !== null && draft?.configID !== undefined &&
      !sameId(draft.configID, current?.configID)) {
    changed.push("configID");
  }
  if (draft?.protocolID !== null && draft?.protocolID !== undefined &&
      !sameId(draft.protocolID, current?.protocolID)) {
    changed.push("protocolID");
  }
  if (isBusTypeChanged(draft?.busType, current?.vehicleSubType)) {
    changed.push("vehicleSubType");
  }
  return changed;
};

/**
 * Continue is enabled when something changed, the remark is non-blank and
 * within maxRemarkLength (the dialog's MAX_REMARK_LENGTH, passed in so this
 * module does not import from a component), and the bus type is valid.
 */
export const isContinueEnabled = ({ changedFields, remark, maxRemarkLength, busTypeError }) =>
  (changedFields?.length ?? 0) > 0 &&
  !!String(remark ?? "").trim() &&
  String(remark ?? "").length <= maxRemarkLength &&
  !busTypeError;

/**
 * Request body for updateDigitalTwinDetails: only the changed critical fields
 * plus remarks. Nothing else, since the backend rejects mixed requests.
 */
export const buildConfigUpdatePayload = (changedFields, draft, remark) => {
  const body = {};
  if (changedFields?.includes("configID")) body.configID = Number(draft.configID);
  if (changedFields?.includes("protocolID")) body.protocolID = Number(draft.protocolID);
  if (changedFields?.includes("vehicleSubType")) body.vehicleSubType = normaliseBusType(draft.busType);
  body.remarks = remark;
  return body;
};

/**
 * Ensures the vehicle's current option is in the list, so the preselected
 * value always has a matching option even when the backend's option list
 * leaves it out (a retired config, a protocol no longer offered).
 */
export const withCurrentOption = (options, currentOption, idKey) => {
  const list = Array.isArray(options) ? options : [];
  const currentId = currentOption?.[idKey];
  if (currentId === null || currentId === undefined) return list;
  return list.some((o) => sameId(o?.[idKey], currentId)) ? list : [currentOption, ...list];
};

export const findOptionById = (options, idKey, id) =>
  (options || []).find((o) => sameId(o?.[idKey], id)) ?? null;

/**
 * The review table rows: only what actually changes. Rated Capacity and
 * Strings follow from the project, so they appear only when the project
 * changes AND their value differs.
 *
 * @returns {Array<{key: string, label: string, from: string, to: string}>}
 */
export const buildReviewRows = ({ current, changedFields, project, protocol, busType }) => {
  const rows = [];
  const changed = new Set(changedFields || []);
  const na = (v) => (v === null || v === undefined || v === "" ? "N/A" : String(v));

  if (changed.has("configID")) {
    rows.push({
      key: "project",
      label: "Project",
      from: na(current?.configName),
      to: na(project?.configName),
    });
    const fromCapacity = formatCapacity(current?.ratedCapacitySystem);
    const toCapacity = formatCapacity(project?.ratedCapacitySystem);
    if (fromCapacity !== toCapacity) {
      rows.push({
        key: "ratedCapacity",
        label: "Rated Capacity",
        from: na(fromCapacity),
        to: na(toCapacity),
      });
    }
    const fromStructure = getCurrentStringStructure(current);
    const toStructure = getStringStructure(project);
    if (isStringStructureChanged(fromStructure, toStructure)) {
      rows.push({
        key: "strings",
        label: "Strings",
        from: formatStrings(fromStructure),
        to: formatStrings(toStructure),
      });
    }
  }
  if (changed.has("protocolID")) {
    rows.push({
      key: "protocol",
      label: "Protocol",
      from: na(current?.protocolName),
      to: na(protocol?.protocolName),
    });
  }
  if (changed.has("vehicleSubType")) {
    rows.push({
      key: "busType",
      label: "Bus Type",
      from: na(current?.vehicleSubType),
      to: busType?.isNew ? `${busType.value} (new)` : na(busType?.value),
    });
  }
  return rows;
};

/**
 * Toast text for a failed load or submit. GetDataV2 lets axios throw on a
 * non-2xx status, so the status and the server message live on
 * error.response; a 200 envelope carrying a non-200 code is rethrown by the
 * view with responseCode set. Either way a 403 reads as a permission message
 * rather than "Request failed with status code 403".
 */
export const getConfigChangeErrorMessage = (error) => {
  const status = error?.response?.status ?? error?.responseCode;
  if (Number(status) === 403) return CRITICAL_UPDATE_FORBIDDEN_MESSAGE;
  // Only a server message is shown. Raw axios text ("Request failed with status
  // code 401", "Network Error") falls back to the generic message.
  const msg = error?.responseCode
    ? error?.message
    : error?.response?.data?.responseStatus?.msg;
  return (msg && translateErrorMessage(msg)) || GENERIC_ERROR_MESSAGE;
};
